import {
  signUp,
  signIn,
  signOut,
  confirmSignUp,
  resetPassword,
  confirmResetPassword,
  getCurrentUser,
  fetchUserAttributes,
  fetchAuthSession,
  resendSignUpCode,
  updateUserAttributes,
} from 'aws-amplify/auth';
import type { User } from '../types/user';

const API_BASE_URL = import.meta.env.VITE_API_GATEWAY_URL || '';

export interface SignUpParams {
  email: string;
  password: string;
  name: string;
}

export interface SignInParams {
  email: string;
  password: string;
}

export interface ConfirmSignUpParams {
  email: string;
  code: string;
}

export interface ResetPasswordParams {
  email: string;
}

export interface ConfirmResetPasswordParams {
  email: string;
  code: string;
  newPassword: string;
}

class AuthService {
  private async getAuthToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch (error) {
      return null;
    }
  }

  private mapAuthError(error: any): never {
    console.error('Cognito auth error:', error);

    // Map Cognito error codes to user-friendly messages
    if (error.name === 'NotAuthorizedException' || error.message?.includes('Incorrect username or password')) {
      throw new Error('Incorrect email or password. Please try again.');
    } else if (error.name === 'UserNotFoundException') {
      throw new Error('No account found with this email address.');
    } else if (error.name === 'UserNotConfirmedException') {
      throw new Error('Please verify your email before signing in.');
    } else if (error.name === 'PasswordResetRequiredException') {
      throw new Error('Password reset required. Please reset your password.');
    } else if (error.name === 'InvalidParameterException') {
      throw new Error('Invalid email or password format.');
    }

    throw new Error(error.message || 'Failed to sign in. Please check your credentials.');
  }

  async signUp({ email, password, name }: SignUpParams) {
    const normalizedEmail = (email || '').trim().toLowerCase();
    try {
      // Use Amplify signUp directly (which uses Cognito)
      const { isSignUpComplete, userId, nextStep } = await signUp({
        username: normalizedEmail,
        password,
        options: {
          userAttributes: {
            email: normalizedEmail,
            name: name ? name.trim() : '',
          },
        },
      });

      return {
        success: true,
        isSignUpComplete,
        userId,
        nextStep,
      };
    } catch (error: any) {
      console.error('Sign up error:', error);
      throw new Error(error.message || 'Failed to sign up');
    }
  }

  async confirmSignUp({ email, code }: ConfirmSignUpParams) {
    const rawEmail = (email || '').trim();
    const lowerEmail = rawEmail.toLowerCase();
    const cleanCode = (code || '').trim();

    try {
      // Try lowercase first
      const { isSignUpComplete, nextStep } = await confirmSignUp({
        username: lowerEmail,
        confirmationCode: cleanCode,
      });

      return {
        success: true,
        isSignUpComplete,
        nextStep,
      };
    } catch (firstError: any) {
      if (firstError.name === 'UserNotFoundException' && rawEmail !== lowerEmail) {
        try {
          const fallback = await confirmSignUp({
            username: rawEmail,
            confirmationCode: cleanCode,
          });
          return {
            success: true,
            isSignUpComplete: fallback.isSignUpComplete,
            nextStep: fallback.nextStep,
          };
        } catch (secondError: any) {
          console.error('Confirm sign up error:', secondError);
          throw new Error(secondError.message || 'Failed to confirm sign up');
        }
      }
      console.error('Confirm sign up error:', firstError);
      throw new Error(firstError.message || 'Failed to confirm sign up');
    }
  }

  async signIn({ email, password }: SignInParams) {
    const rawEmail = (email || '').trim();
    const lowerEmail = rawEmail.toLowerCase();

    // Strategy: Try lowercase first (as users in Cognito are onboarded in lowercase).
    // If UserNotFoundException occurs and the raw entered username differs in casing, fallback to raw casing.
    try {
      const amplifyResult = await signIn({
        username: lowerEmail,
        password,
      });

      return {
        success: true,
        isSignedIn: amplifyResult.isSignedIn,
        nextStep: amplifyResult.nextStep,
      };
    } catch (firstError: any) {
      if (firstError.name === 'UserNotFoundException' && rawEmail !== lowerEmail) {
        try {
          const fallbackResult = await signIn({
            username: rawEmail,
            password,
          });
          return {
            success: true,
            isSignedIn: fallbackResult.isSignedIn,
            nextStep: fallbackResult.nextStep,
          };
        } catch (secondError: any) {
          this.mapAuthError(secondError);
        }
      }
      this.mapAuthError(firstError);
    }
  }

  async signOut() {
    try {
      await signOut();
      return { success: true };
    } catch (error: any) {
      console.error('Sign out error:', error);
      throw new Error(error.message || 'Failed to sign out');
    }
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const { username, userId } = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      
      // Fetch user's Cognito groups
      const session = await fetchAuthSession();
      const groups = session.tokens?.accessToken?.payload['cognito:groups'] as string[] || [];
      
      // Map Cognito group to role (Admin, Trainer, Student)
      let role: 'admin' | 'instructor' | 'student' = 'student';
      if (groups.includes('Admin')) {
        role = 'admin';
      } else if (groups.includes('Trainer') || groups.includes('Instructor')) {
        role = 'instructor';
      } else if (groups.includes('Student')) {
        role = 'student';
      }

      // Optionally fetch additional user data from your API
      try {
        const token = await this.getAuthToken();
        if (token) {
          const response = await fetch(`${API_BASE_URL}/user`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const userData = await response.json();
            // Merge API user data with Cognito data
            return {
              id: userId,
              email: attributes.email || userData.email || '',
              name: attributes.name || userData.name || username,
              role,
              groups, // Include Cognito groups
              avatar: attributes.picture || userData.avatar,
              createdAt: userData.createdAt || new Date().toISOString(),
            };
          }
        }
      } catch (apiError) {
        console.warn('Failed to fetch additional user data from API:', apiError);
      }

      // Fallback to Cognito data only
      const user: User = {
        id: userId,
        email: attributes.email || '',
        name: attributes.name || username,
        role,
        groups, // Include Cognito groups
        avatar: attributes.picture,
        createdAt: new Date().toISOString(),
      };

      return user;
    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  }

  async resetPassword({ email }: ResetPasswordParams) {
    const rawEmail = (email || '').trim();
    const lowerEmail = rawEmail.toLowerCase();

    try {
      const output = await resetPassword({ username: lowerEmail });
      return {
        success: true,
        nextStep: output.nextStep,
      };
    } catch (firstError: any) {
      if (firstError.name === 'UserNotFoundException' && rawEmail !== lowerEmail) {
        try {
          const fallback = await resetPassword({ username: rawEmail });
          return {
            success: true,
            nextStep: fallback.nextStep,
          };
        } catch (secondError: any) {
          console.error('Reset password error:', secondError);
          throw new Error(secondError.message || 'Failed to reset password');
        }
      }
      console.error('Reset password error:', firstError);
      throw new Error(firstError.message || 'Failed to reset password');
    }
  }

  async confirmResetPassword({ email, code, newPassword }: ConfirmResetPasswordParams) {
    const rawEmail = (email || '').trim();
    const lowerEmail = rawEmail.toLowerCase();
    const cleanCode = (code || '').trim();

    try {
      await confirmResetPassword({
        username: lowerEmail,
        confirmationCode: cleanCode,
        newPassword,
      });

      return { success: true };
    } catch (firstError: any) {
      if (firstError.name === 'UserNotFoundException' && rawEmail !== lowerEmail) {
        try {
          await confirmResetPassword({
            username: rawEmail,
            confirmationCode: cleanCode,
            newPassword,
          });
          return { success: true };
        } catch (secondError: any) {
          console.error('Confirm reset password error:', secondError);
          throw new Error(secondError.message || 'Failed to confirm reset password');
        }
      }
      console.error('Confirm reset password error:', firstError);
      throw new Error(firstError.message || 'Failed to confirm reset password');
    }
  }

  async checkAuthStatus(): Promise<boolean> {
    try {
      await getCurrentUser();
      return true;
    } catch {
      return false;
    }
  }

  async resendConfirmationCode({ email }: { email: string }) {
    const rawEmail = (email || '').trim();
    const lowerEmail = rawEmail.toLowerCase();

    try {
      const output = await resendSignUpCode({ username: lowerEmail });
      return {
        success: true,
        codeDeliveryDetails: (output as any).codeDeliveryDetails,
      };
    } catch (firstError: any) {
      if (firstError.name === 'UserNotFoundException' && rawEmail !== lowerEmail) {
        try {
          const fallback = await resendSignUpCode({ username: rawEmail });
          return {
            success: true,
            codeDeliveryDetails: (fallback as any).codeDeliveryDetails,
          };
        } catch (secondError: any) {
          console.error('Resend confirmation code error:', secondError);
          throw new Error(secondError.message || 'Failed to resend confirmation code');
        }
      }
      console.error('Resend confirmation code error:', firstError);
      throw new Error(firstError.message || 'Failed to resend confirmation code');
    }
  }

  async updateUserSession(sessionId: string) {
    try {
      await updateUserAttributes({
        userAttributes: {
          nickname: sessionId,
        },
      });
      return { success: true };
    } catch (error: any) {
      console.error('Update user session attribute error:', error);
      throw new Error(error.message || 'Failed to update session attribute');
    }
  }

  async getUserAttributes() {
    try {
      return await fetchUserAttributes();
    } catch (error: any) {
      console.error('Fetch user attributes error:', error);
      throw new Error(error.message || 'Failed to fetch user attributes');
    }
  }
}

export const authService = new AuthService();
